import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { Receipt } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { formatCurrency } from '@/lib/utils/currency';
import { useBillMutations } from '../hooks';
import type { Bill } from '../types';
import type { Currency } from '@/features/contracts/types';

interface BillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → manual create; a bill → edit (DRAFT only) */
  bill: Bill | null;
}

/**
 * Create a manual bill or edit a DRAFT bill. Editable fields: contract (manual
 * create only — shown read-only on edit), base amount, description. The
 * currency always follows the selected contract (read-only).
 */
export function BillDialog({ open, onOpenChange, bill }: BillDialogProps) {
  const { t } = useTranslation();
  const { createMutation, updateMutation } = useBillMutations();
  const isEdit = !!bill;

  const [contractId, setContractId] = React.useState<number | undefined>(undefined);
  const [amount, setAmount] = React.useState('');
  // Display-only — derived from the contract (or the bill on edit).
  const [currency, setCurrency] = React.useState<Currency>('USD');
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setContractId(bill?.contractId ?? undefined);
      setAmount(
        bill?.totalWithoutDiscount != null
          ? String(bill.totalWithoutDiscount)
          : bill?.total != null
            ? String(bill.total)
            : '',
      );
      setCurrency((bill?.currency as Currency) ?? 'USD');
      setDescription(bill?.description ?? '');
    }
  }, [open, bill]);

  const amountNum = Number(amount);
  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    amount.trim() !== '' &&
    Number.isFinite(amountNum) &&
    amountNum >= 0 &&
    (isEdit || !!contractId) &&
    !isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      if (isEdit && bill) {
        await updateMutation.mutateAsync({
          id: bill.id,
          data: {
            totalWithoutDiscount: amountNum,
            description: description.trim() || undefined,
          },
        });
      } else {
        await createMutation.mutateAsync({
          contractId: contractId as number,
          totalWithoutDiscount: amountNum,
          description: description.trim() || undefined,
        });
      }
      onOpenChange(false);
    } catch {
      // errors surfaced via the mutation's onError toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-slate-500" />
            {isEdit ? t('billForm.editDialogTitle') : t('billForm.createDialogTitle')}
          </DialogTitle>
          <DialogDescription>{t('billForm.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">{t('contractName')}</label>
            <UniversalCombobox
              url="/contracts"
              labelKey="name"
              placeholder={t('billForm.contractPlaceholder')}
              queryKey="billContractCombo"
              selectedId={contractId}
              disabled={isEdit}
              renderLabel={(item) => {
                const name = (item?.name as string) ?? `#${String(item?.id ?? '')}`;
                const status = item?.status ? t(`status.${item.status}`) : '';
                return status ? `${name} — ${status}` : name;
              }}
              onSelectItem={(item) => {
                setContractId(item?.id as number | undefined);
                if (item?.currency) setCurrency(item.currency as Currency);
              }}
            />
            {isEdit && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('billForm.contractLockedHint')}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('billForm.amountLabel')}</label>
            <NumericFormat
              customInput={Input}
              thousandSeparator=" "
              allowNegative={false}
              decimalScale={0}
              value={amount}
              onValueChange={(v) => setAmount(v.value ?? '')}
              placeholder="0"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('billForm.currencyFollowsContract', { currency })}
            </p>
          </div>

          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {t('total')}:{' '}
            <span className="font-semibold text-slate-900">
              {formatCurrency(canSubmit ? amountNum : 0, { currency })}
            </span>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('billForm.descriptionLabel')}</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder={t('billForm.descriptionPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isPending ? t('processing') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
